import { Form } from 'react-bootstrap';
import Modal from '../../components/modals/Modal';
import RiskMatrix from '../../components/RiskMatrix';
import { useModal } from '../../providers/ModalProvider';
import { modals } from '../../providers/modals';
import styles from './AddRiskModal.module.scss';
import { Text } from '../../components/typography';
import { loader } from 'graphql.macro';
import { useMutation } from '@apollo/client';
import { useState } from 'react';
import Mitigations from './Mitigations';
import Select from 'react-select';
import { useWorkspace } from '../../providers/WorkspaceProvider';
import TextWithIcon from '../../components/typography/TextWithIcon';
import { riskTypes } from '../../utilities/risk_registry';
import { showToast } from '../../graphql/cache/modal';

const addRiskMutation = loader('./NewRiskList.add.graphql');

export default function AddRiskModal() {
  const { modalState, closeModal, updateModal } = useModal();
  const { availableWorkspaces, workspace } = useWorkspace();
  const [addRisk] = useMutation(addRiskMutation, {
    refetchQueries: ['GetRiskFromRegistry'],
  });
  const [state, setState] = useState({});
  const {
    show,
    impact,
    probability,
    description,
    workspaceId = workspace?.id,
  } = modalState.addRiskModal;
  const onHide = () => {
    closeModal({ modalName: modals.addRisk });
    setState({});
  };
  const update = (variables) =>
    updateModal({
      modalName: modals.addRisk,
      variables: { ...variables },
    });

  return (
    <Modal
      title={'⚠️  Add New Hazard'}
      open={show}
      onClose={onHide}
      submitDisabled={!description || !impact || !probability || !state?.type}
      onSubmit={async () => {
        const mitigations = Object.values(state)
          .flat()
          .map((s) => s.value)
          .filter(Boolean);
        await addRisk({
          variables: {
            ...modalState.addRiskModal,
            mitigations,
            workspaceId,
            type: state.type,
          },
        }).then(() =>
          showToast({
            title: 'New Risk Added',
            message: 'Risk Successfully added.',
          }),
        );
      }}
    >
      <div className={styles.content}>
        {availableWorkspaces.length > 1 && (
          <div className={styles.workspace}>
            <TextWithIcon required weight="bold" noMargin>
              Workspace
            </TextWithIcon>
            <Select
              value={
                state.workspace || {
                  label: workspace?.title,
                  value: workspace?.value,
                }
              }
              options={availableWorkspaces.map((w) => ({
                label: w.title,
                value: w.id,
                ...w,
              }))}
              onChange={(w) => {
                update({ workspaceId: w.id });
                setState({
                  ...state,
                  workspace: { label: w.title, value: w.id },
                });
              }}
            />
          </div>
        )}
        <TextWithIcon required weight="bold" noMargin>
          Hazard Description<span className={styles.red}>*</span>
        </TextWithIcon>
        <Form.Control
          value={description}
          as="textarea"
          rows={3}
          onChange={(e) => update({ description: e.target.value })}
        />
        <Mitigations
          required
          isEditing={true}
          setState={setState}
          state={state}
          inline={false}
          showProps={{ threats: true, consequences: true }}
        />
        <div className={styles.spacer} />
        <div>
          <TextWithIcon required weight="bold" noMargin>
            Risk Type<span className={styles.red}>*</span>
          </TextWithIcon>
          <Text size="sm" noMargin>
            Select the type with the highest risk
          </Text>
        </div>
        <Select
          options={Object.values(riskTypes)}
          onChange={(type) => setState({ ...state, type: type.value })}
        />
        <div className={styles.spacer} />
        <TextWithIcon required weight="bold" noMargin>
          Initial Risk Assessment<span className={styles.red}>*</span>
        </TextWithIcon>
        <RiskMatrix
          showMessage={false}
          showAddToRegistry={false}
          impact={impact}
          probability={probability}
          onChange={(impact, probability) => update({ impact, probability })}
        />
      </div>
    </Modal>
  );
}
